import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Spinner, Button, Card, CardBody } from 'reactstrap';
import { Search, RefreshCw, Package, AlertTriangle, XCircle, Filter, ChevronLeft, ChevronRight, Download } from 'react-feather';
import http from '../../../components/network';
import StatusBadge from '../components/StatusBadge';
import downloadExcel from '../../../utils/downloadExcel';

const StockOnHand = () => {
  const { t } = useTranslation();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [hideZero, setHideZero] = useState(false);
  const [lowStockOnly, setLowStockOnly] = useState(false);

  // ── Pagination ──
  const [page, setPage] = useState(1);
  const [perPage] = useState(25);

  const [exporting, setExporting] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await http.get('/inventory/stock-on-hand', {
        params: {
          search: search || undefined,
          hide_zero: hideZero ? 1 : undefined,
          low_stock_only: lowStockOnly ? 1 : undefined,
        },
      });
      const sortedData = (response.data || []).sort((a, b) =>
        (a.item?.item_name || '').localeCompare(b.item?.item_name || '', undefined, { sensitivity: 'base' })
      );
      setData(sortedData);
    } catch (error) {
      console.error('Error fetching stock on hand:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hideZero, lowStockOnly]);

  // Reset page setiap filter berubah
  useEffect(() => {
    setPage(1);
  }, [search, hideZero, lowStockOnly]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchData();
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await downloadExcel(
        '/inventory/export/stock-on-hand',
        {
          search: search || undefined,
          hide_zero: hideZero ? 1 : undefined,
          low_stock_only: lowStockOnly ? 1 : undefined,
        },
        'stock-on-hand.xlsx'
      );
    } catch (err) {
      alert('Gagal export Excel');
    } finally {
      setExporting(false);
    }
  };

  const handleReset = () => {
    setSearch('');
    setHideZero(false);
    setLowStockOnly(false);
    setPage(1);
    setTimeout(fetchData, 50);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('id-ID').format(parseFloat(num) || 0);
  };

  // ── Pagination calculations ──
  const totalPages = Math.ceil(data.length / perPage);
  const startIdx = (page - 1) * perPage;
  const endIdx = startIdx + perPage;
  const paginatedData = data.slice(startIdx, endIdx);

  // Summary dari data
  const totalItems = data.length;
  const lowStockCount = data.filter((r) => r.status === 'low').length;
  const outOfStockCount = data.filter((r) => r.status === 'out').length;

  return (
    <div>
      {/* ═══════ SUMMARY CARDS ═══════ */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '12px',
          marginBottom: '16px',
        }}
      >
        <Card className="border-0 shadow-sm" style={{ borderLeft: '4px solid #3b82f6' }}>
          <CardBody className="p-3">
            <div className="d-flex align-items-center gap-2 mb-2">
              <Package size={14} className="text-primary" />
              <small
                className="text-muted text-uppercase fw-bold"
                style={{ fontSize: '10px', letterSpacing: '0.5px' }}
              >
                {t('Total Items')}
              </small>
            </div>
            <h4 className="fw-bold mb-0 text-primary">{totalItems}</h4>
          </CardBody>
        </Card>

        <Card className="border-0 shadow-sm" style={{ borderLeft: '4px solid #f59e0b' }}>
          <CardBody className="p-3">
            <div className="d-flex align-items-center gap-2 mb-2">
              <AlertTriangle size={14} className="text-warning" />
              <small
                className="text-muted text-uppercase fw-bold"
                style={{ fontSize: '10px', letterSpacing: '0.5px' }}
              >
                {t('Low Stock')}
              </small>
            </div>
            <h4 className="fw-bold mb-0 text-warning">{lowStockCount}</h4>
          </CardBody>
        </Card>

        <Card className="border-0 shadow-sm" style={{ borderLeft: '4px solid #ef4444' }}>
          <CardBody className="p-3">
            <div className="d-flex align-items-center gap-2 mb-2">
              <XCircle size={14} className="text-danger" />
              <small
                className="text-muted text-uppercase fw-bold"
                style={{ fontSize: '10px', letterSpacing: '0.5px' }}
              >
                {t('Out of Stock')}
              </small>
            </div>
            <h4 className="fw-bold mb-0 text-danger">{outOfStockCount}</h4>
          </CardBody>
        </Card>
      </div>

      {/* ═══════ FILTER CARD ═══════ */}
      <Card className="border-0 shadow-sm mb-3">
        <CardBody className="p-3">
          <div className="d-flex align-items-center gap-2 mb-3">
            <div
              className="d-flex align-items-center justify-content-center rounded"
              style={{ width: '32px', height: '32px', backgroundColor: '#eff6ff', color: '#3b82f6' }}
            >
              <Filter size={16} />
            </div>
            <div>
              <h6 className="fw-bold mb-0" style={{ color: '#1e293b' }}>{t('Filter')}</h6>
              <small className="text-muted">{t('Search and filter stock items')}</small>
            </div>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '3fr 2fr 2fr 1fr',
              gap: '12px',
              alignItems: 'end',
            }}
          >
            {/* Search */}
            <div>
              <div
                className="small fw-bold text-muted text-uppercase"
                style={{ fontSize: '10px', letterSpacing: '0.5px', marginBottom: '6px' }}
              >
                <Search size={12} className="me-1" />
                {t('Search Item')}
              </div>
              <form onSubmit={handleSearch}>
                <Input
                  type="text"
                  placeholder={t('Search item name...')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ borderRadius: '8px', fontSize: '13px', height: '38px' }}
                />
              </form>
            </div>

            {/* Hide Zero */}
            <div>
              <div
                className="small fw-bold text-muted text-uppercase"
                style={{ fontSize: '10px', letterSpacing: '0.5px', marginBottom: '6px' }}
              >
                {t('Options')}
              </div>
              <div
                className="d-flex align-items-center"
                style={{
                  height: '38px',
                  padding: '0 12px',
                  border: '1px solid #dee2e6',
                  borderRadius: '8px',
                  fontSize: '13px',
                }}
              >
                <input
                  type="checkbox"
                  id="hideZero"
                  checked={hideZero}
                  onChange={(e) => setHideZero(e.target.checked)}
                  className="me-2"
                />
                <label htmlFor="hideZero" className="mb-0" style={{ cursor: 'pointer' }}>
                  {t('Hide zero stock')}
                </label>
              </div>
            </div>

            {/* Low Stock Only */}
            <div>
              <div
                className="small fw-bold text-muted text-uppercase"
                style={{ fontSize: '10px', letterSpacing: '0.5px', marginBottom: '6px' }}
              >
                &nbsp;
              </div>
              <div
                className="d-flex align-items-center"
                style={{
                  height: '38px',
                  padding: '0 12px',
                  border: '1px solid #dee2e6',
                  borderRadius: '8px',
                  fontSize: '13px',
                }}
              >
                <input
                  type="checkbox"
                  id="lowStockOnly"
                  checked={lowStockOnly}
                  onChange={(e) => setLowStockOnly(e.target.checked)}
                  className="me-2"
                />
                <label htmlFor="lowStockOnly" className="mb-0" style={{ cursor: 'pointer' }}>
                  {t('Low stock only')}
                </label>
              </div>
            </div>

            {/* Button */}
            {/* Button */}
            <div>
              <div style={{ fontSize: '10px', marginBottom: '6px' }}>&nbsp;</div>
              <div className="d-flex gap-2">
                <Button
                  color="primary"
                  type="button"
                  onClick={handleSearch}
                  style={{ borderRadius: '8px', fontWeight: 600, height: '38px', flex: 1 }}
                >
                  <Search size={14} className="me-1" />
                  {t('Search')}
                </Button>
                <Button
                  color="light"
                  onClick={handleReset}
                  style={{
                    borderRadius: '8px',
                    height: '38px',
                    border: '1px solid #dee2e6',
                    padding: '0 12px',
                  }}
                  title={t('Reset')}
                >
                  <RefreshCw size={14} />
                </Button>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* ═══════ TABLE ═══════ */}
      <Card className="border-0 shadow-sm">
        <CardBody className="p-0">
          <div className="p-3 border-bottom d-flex align-items-center justify-content-between">
            <div>
              <h6 className="fw-bold mb-0" style={{ color: '#1e293b' }}>
                {t('Stock on Hand')}
              </h6>
              <small className="text-muted">
                {data.length} {t('items')}
              </small>
            </div>
            <Button
              color="success"
              size="sm"
              onClick={handleExport}
              disabled={exporting}
              className="d-flex align-items-center gap-1"
              style={{ borderRadius: '8px', fontWeight: 600 }}
            >
              {exporting ? <Spinner size="sm" /> : <><Download size={14} /> {t('Export Excel')}</>}
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              {/* Header */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '3fr 100px 130px 100px 110px 140px 140px',
                  gap: 0,
                  background: '#f8fafc',
                  borderBottom: '1px solid #e2e8f0',
                  fontSize: '11px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  color: '#6b7280',
                  fontWeight: 700,
                  minWidth: '950px',
                }}
              >
                <div style={{ padding: '10px 16px' }}>{t('Item')}</div>
                <div style={{ padding: '10px 16px', textAlign: 'center' }}>{t('Unit')}</div>
                <div style={{ padding: '10px 16px', textAlign: 'right' }}>{t('Stock')}</div>
                <div style={{ padding: '10px 16px', textAlign: 'right' }}>{t('Min')}</div>
                <div style={{ padding: '10px 16px', textAlign: 'center' }}>{t('Status')}</div>
                <div style={{ padding: '10px 16px', textAlign: 'center' }}>{t('Expired')}</div>
                <div style={{ padding: '10px 16px', textAlign: 'center' }}>{t('Last Move')}</div>
              </div>

              {/* Body */}
              {paginatedData.length === 0 ? (
                <div className="text-center text-muted py-5">
                  <Package size={32} style={{ opacity: 0.3 }} className="mb-2" />
                  <div>{t('No stock data')}</div>
                </div>
              ) : (
                paginatedData.map((row, idx) => (
                  <div
                    key={`${row.item_id}-${row.item_unit_id || 'unit'}`}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '3fr 100px 130px 100px 110px 140px 140px',
                      gap: 0,
                      borderBottom: '1px solid #f1f5f9',
                      fontSize: '13px',
                      background: idx % 2 === 0 ? '#fff' : '#fafafa',
                      minWidth: '950px',
                    }}
                  >
                    <div
                      style={{
                        padding: '10px 16px',
                        fontWeight: 600,
                        color: '#1e293b',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={row.item?.item_name || '-'}
                    >
                      {row.item?.item_name || '-'}
                    </div>
                    <div style={{ padding: '10px 16px', textAlign: 'center', color: '#6b7280', fontWeight: 600 }}>
                      {row.item?.unit_item?.unit_name || row.item_unit?.unit_name || '-'}
                    </div>
                    <div
                      style={{
                        padding: '10px 16px',
                        textAlign: 'right',
                        fontWeight: 700,
                        color: '#1e293b',
                      }}
                    >
                      {formatNumber(row.available_stock)}
                    </div>
                    <div style={{ padding: '10px 16px', textAlign: 'right', color: '#6b7280' }}>
                      {formatNumber(row.min_stock)}
                    </div>
                    <div style={{ padding: '10px 16px', textAlign: 'center' }}>
                      <StatusBadge status={row.status} />
                    </div>
                    <div
                      style={{
                        padding: '10px 16px',
                        textAlign: 'center',
                        color: '#6b7280',
                        fontSize: '12px',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {row.nearest_expired
                        ? new Date(row.nearest_expired).toLocaleDateString('id-ID', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                        })
                        : '-'}
                    </div>
                    <div
                      style={{
                        padding: '10px 16px',
                        textAlign: 'center',
                        color: '#6b7280',
                        fontSize: '12px',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {row.last_movement
                        ? new Date(row.last_movement).toLocaleDateString('id-ID', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                        })
                        : '-'}
                    </div>
                  </div>
                ))
              )}

              {/* ═══════ PAGINATION ═══════ */}
              {data.length > 0 && (
                <div
                  className="d-flex justify-content-between align-items-center p-3"
                  style={{
                    borderTop: '1px solid #e2e8f0',
                    minWidth: '950px',
                    background: '#fff',
                  }}
                >
                  <small className="text-muted">
                    {t('Showing')} <strong>{startIdx + 1}</strong>–<strong>{Math.min(endIdx, data.length)}</strong> {t('of')} <strong>{data.length}</strong> {t('items')}
                  </small>

                  <div className="d-flex align-items-center gap-2">
                    <Button
                      size="sm"
                      color="light"
                      className="border d-flex align-items-center"
                      disabled={page === 1}
                      onClick={() => setPage(page - 1)}
                      style={{ borderRadius: '6px' }}
                    >
                      <ChevronLeft size={14} />
                    </Button>

                    <span className="text-muted small px-2">
                      {t('Page')} <strong>{page}</strong> {t('of')} <strong>{totalPages || 1}</strong>
                    </span>

                    <Button
                      size="sm"
                      color="light"
                      className="border d-flex align-items-center"
                      disabled={page === totalPages || totalPages === 0}
                      onClick={() => setPage(page + 1)}
                      style={{ borderRadius: '6px' }}
                    >
                      <ChevronRight size={14} />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default StockOnHand;